import { useState, useEffect, useRef } from "react";
import { useDealer } from "./useDealer";
import { useApiClient, useSafeApiCall } from "./api";
import { API_PREFIX_PATH } from "src/constants";
import { downloadDocument } from "src/utils";

interface UseLenderDocumentsReturn {
  hasLenderDocuments: boolean;
  loading: boolean;
  downloadLenderDocument: (
    fallbackFilename?: string,
    viewOnly?: boolean,
  ) => Promise<void>;
}

export const useLenderDocuments = (
  applicationId: string,
  lenderId: string,
): UseLenderDocumentsReturn => {
  const { dealer_id } = useDealer();
  const [hasLenderDocuments, setHasLenderDocuments] = useState(false);
  const [loading, setLoading] = useState(false);
  const cachedBlobRef = useRef<Blob | null>(null);
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();

  useEffect(() => {
    if (!dealer_id || !applicationId || !lenderId) {
      setHasLenderDocuments(false);
      setLoading(false);
      cachedBlobRef.current = null;
      return;
    }

    setHasLenderDocuments(false);
    cachedBlobRef.current = null;
    setLoading(true);

    const path = `${API_PREFIX_PATH}/${dealer_id}/application-documents/${applicationId}/lender/${lenderId}`;

    safeApiCall(() => fetchFromApi(path, { responseType: "blob" }))
      .then((response) => {
        const res = response as { blob?: Blob } | Blob;
        const blob = "blob" in res && res.blob ? res.blob : (res as Blob);
        cachedBlobRef.current = blob;
        setHasLenderDocuments(true);
        setLoading(false);
      })
      .catch(() => {
        setHasLenderDocuments(false);
        setLoading(false);
        cachedBlobRef.current = null;
      });

    return () => {
      cachedBlobRef.current = null;
    };
  }, [applicationId, lenderId, dealer_id]);

  const downloadLenderDocument = async (
    fallbackFilename?: string,
    viewOnly = false,
  ) => {
    if (!dealer_id || !applicationId || !lenderId) return;

    const path = `${API_PREFIX_PATH}/${dealer_id}/application-documents/${applicationId}/lender/${lenderId}`;
    await downloadDocument(
      fetchFromApi,
      safeApiCall,
      path,
      fallbackFilename,
      viewOnly,
    );
  };

  return {
    hasLenderDocuments,
    loading,
    downloadLenderDocument,
  };
};
